/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { PathContext } from '@/components/sidebar/types';
import { currentPlatform, isEntrepreneurPath } from '@/utils/paths';

interface SidebarContextType {
  isCollapsed: boolean;
  setIsCollapsed: (value: boolean) => void;
  pathContext: PathContext;
  currentPath: string;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export function SidebarProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = localStorage.getItem('sidebarCollapsed');
    return stored ? JSON.parse(stored) : true;
  });

  const detectPathContext = (pathname: string): PathContext => {
    if (isEntrepreneurPath(pathname)) return 'entrepreneurs';
    // Any platform — host-served or prefixed — is a staff surface.
    if (currentPlatform()) return 'admin';
    return 'root';
  };

  const pathContext = detectPathContext(location.pathname);
  const currentPath = location.pathname;

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  return (
    <SidebarContext.Provider value={{ 
      isCollapsed, 
      setIsCollapsed, 
      pathContext, 
      currentPath 
    }}>
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}